import React, { useState, useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useParams } from "react-router-dom";
import {
  Box,
  Typography,
  TextField,
  Button,
  FormControl,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Divider,
  Chip,
  CircularProgress,
  Snackbar,
  Alert,
  alpha,
  Popover,
  InputAdornment,
  Card,
  CardContent,
  IconButton,
  Breadcrumbs,
  Link,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import CloudIcon from "@mui/icons-material/Cloud";
import PublicIcon from "@mui/icons-material/Public";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import SearchIcon from "@mui/icons-material/Search";
import {
  getCustomerMetadata,
  clearBillingManagementMessages,
} from "../../store/billingManagement/billingManagementSlice";
import { getCustomerMetadata as getCustomerMetadataApi } from "../../helpers/auth_aws_helper";
import { useBillingCustomerOrgDisplay } from "./useBillingCustomerOrgDisplay";
import {
  normalizeMetadataCodeList,
  getEligibleServiceCodesFromApi,
  partitionSelectedServicesForAwsUsage,
  ADJUSTMENT_PARTITION_ELIGIBLE,
  ADJUSTMENT_PARTITION_INELIGIBLE,
  migrateSelectedRulesAdjustmentPartitions,
  isDeprecatedAdjustmentRuleType,
} from "./awsUsageServiceScopePartitions";
import AwsPartnerSupportRuleFields from "./AwsPartnerSupportRuleFields";
import {
  buildAwsPartnerSupportDefaultParameters,
  validateAwsPartnerSupportParameters,
  SUPPORT_RULE_CATEGORY,
  AWS_PARTNER_SUPPORT_RULE_TYPE,
  buildAwsPartnerSupportMatrixFromParameters,
  getActivePlanParametersFromMatrix,
} from "./awsPartnerSupportBilling";
import {
  APPLIES_TO_RULE_TYPES_KEY,
  buildAppliesToRuleTypesMapFromSelection,
  buildDefaultAppliesToRuleTypesMap,
  buildDefaultRuleParametersFromTypeDef,
  getAppliesToRuleTypeOptions,
  getSelectedAppliesToRuleTypeValues,
  isTaxesStandaloneCategory,
} from "./billingTaxRuleParameters";
import AppliesToRuleTypesSelect from "./AppliesToRuleTypesSelect";

const MAX_RULE_BLOCKS = 10;

/**
 * Temporary: hide only the standalone `TAXES` category from rule_definitions (label "Taxes", e.g. CUSTOM_TAX).
 * Does not remove `TAX` under `TRANSFER` ("Transfers").
 */
const HIDE_TAXES_CATEGORY_IN_CREATE_UI = false;

/** Default-on tax is TRANSFER / TAX, not standalone TAXES / CUSTOM_TAX. */
function findTransferTaxRuleType(categories) {
  if (!Array.isArray(categories)) return null;
  const transferCat = categories.find((c) => String(c.category || "").toUpperCase() === "TRANSFER");
  if (!transferCat) return null;
  const taxType = (transferCat.types || []).find((t) => String(t.type || "").toUpperCase() === "TAX");
  if (!taxType) return null;
  return { taxCat: transferCat, taxType };
}

const BILLING_MONTH_OPTIONS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

/** Same outer frame as AWS Partner Led Support rule row (CreateBillingRule `renderRuleTypePairRow` partner block). */
const PARTNER_LED_RULE_OUTER_SX = {
  p: 2,
  border: "1px solid #e2e8f0",
  borderRadius: 1.5,
  backgroundColor: "#ffffff",
  "&:hover": { borderColor: "#cbd5e1" },
};

/** Same title line as "Select a Support Plan" in Partner Led Support. */
const PARTNER_LED_RULE_PRIMARY_TITLE_SX = {
  fontSize: "0.9375rem",
  fontWeight: 600,
  color: "#1e293b",
  lineHeight: 1.35,
};

const freqSelectOutlined = {
  fontSize: "0.8125rem",
  backgroundColor: "#fafbfc",
  borderRadius: "8px",
  height: 36,
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e2e8f0" },
  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#cbd5e1" },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "#3b82f6",
    borderWidth: 1,
  },
  "& .MuiSelect-select": { py: 0.875, px: 1 },
};

const freqMonthSelectSx = {
  ...freqSelectOutlined,
  width: 132,
  minWidth: 132,
  flexShrink: 0,
};
const freqYearSelectSx = {
  ...freqSelectOutlined,
  width: 80,
  minWidth: 80,
  flexShrink: 0,
};

/** Eligible / Ineligible partition accordions + Filters bar (same header background). */
const ADJUSTMENT_PARTITION_HEADER_BG = "#f8fafc";
const ADJUSTMENT_PARTITION_ACCORDION_SX = {
  border: "1px solid #e2e8f0",
  borderRadius: 1.5,
  overflow: "hidden",
  backgroundColor: "#ffffff",
  boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
  "&:before": { display: "none" },
};
const ADJUSTMENT_PARTITION_SUMMARY_SX = {
  px: 2,
  minHeight: 48,
  backgroundColor: ADJUSTMENT_PARTITION_HEADER_BG,
  borderBottom: "1px solid #e2e8f0",
  "& .MuiAccordionSummary-content": { my: 1, alignItems: "center" },
};
const ADJUSTMENT_FILTERS_BAR_SX = {
  px: 1.5,
  py: 1,
  backgroundColor: ADJUSTMENT_PARTITION_HEADER_BG,
  borderBottom: "1px solid #e2e8f0",
};

const CreateBillingRule = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { customerOrgCode } = useParams();

  // Redux state
  const {
    metadata,
    isFetchingMetadata,
    fetchMetadataError,
  } = useSelector((state) => state.billingManagement);

  const { displayName: orgDisplayName } = useBillingCustomerOrgDisplay(customerOrgCode, { enabled: true });

  // Storage key for persisting rule blocks
  const STORAGE_KEY = `billing_rule_blocks_${customerOrgCode}`;

  // Load initial state from sessionStorage or use default
  const getInitialRuleBlocks = () => {
    try {
      const saved = sessionStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        // Validate that it's an array and has at least one block
        if (Array.isArray(parsed) && parsed.length > 0) {
          // Migrate old state: convert linked_accounts to selectedAccounts/allAccounts
          return parsed.map((block) => {
            let migratedBlock = { ...block };
            
            // If old state has linked_accounts, migrate it
            if (block.linked_accounts !== undefined) {
              const allAccounts = block.linked_accounts === "ALL";
              const selectedAccounts = allAccounts ? [] : (block.linked_accounts ? [block.linked_accounts] : []);
              migratedBlock = {
                ...migratedBlock,
                allAccounts,
                selectedAccounts,
                linked_accounts: undefined, // Remove old field
              };
            }
            
            // Ensure selectedAccounts exists and is an array
            // Default allAccounts to true if undefined (for backward compatibility and default behavior)
            const hasSelectedAccounts = Array.isArray(migratedBlock.selectedAccounts) && migratedBlock.selectedAccounts.length > 0;
            // If allAccounts is undefined, default to true if no accounts are selected
            const defaultAllAccounts = migratedBlock.allAccounts !== undefined ? migratedBlock.allAccounts : !hasSelectedAccounts;
            migratedBlock = {
              ...migratedBlock,
              allAccounts: defaultAllAccounts,
              selectedAccounts: Array.isArray(migratedBlock.selectedAccounts) ? migratedBlock.selectedAccounts : [],
            };
            
            // Fix frequency: non-CUSTOM categories must be RECURRING
            if (migratedBlock.selectedCategory && migratedBlock.selectedCategory !== "CUSTOM" && migratedBlock.frequency === "ONE_TIME") {
              migratedBlock.frequency = "RECURRING";
              migratedBlock.start_date = migratedBlock.start_date || "";
              migratedBlock.end_date = migratedBlock.end_date || "";
            }
            // Migrate to selectedRules (multi-select rule types)
            if (!Array.isArray(migratedBlock.selectedRules)) {
              const selectedRules = [];
              if (migratedBlock.selectedTypes && migratedBlock.selectedTypes.length > 0) {
                migratedBlock.selectedTypes.forEach((type) => {
                  selectedRules.push({
                    category: migratedBlock.rule_category,
                    type,
                    parameters: { enabled: true },
                  });
                });
              } else if (migratedBlock.rule_category && migratedBlock.rule_type) {
                selectedRules.push({
                  category: migratedBlock.rule_category,
                  type: migratedBlock.rule_type,
                  parameters: migratedBlock.parameters || {},
                });
              }
              migratedBlock = { ...migratedBlock, selectedRules };
            }
            // Migrate legacy customRuleDescription into CUSTOM rule parameters.
            // Old UI had a single description field shared by all Custom rules; new UI needs per-row descriptions.
            // Apply the same description to every CUSTOM rule that doesn't already have one.
            const customRules = (migratedBlock.selectedRules || []).filter((r) => r.category === "CUSTOM");
            if (customRules.length > 0 && migratedBlock.customRuleDescription != null && String(migratedBlock.customRuleDescription).trim() !== "") {
              const desc = migratedBlock.customRuleDescription.trim();
              migratedBlock = {
                ...migratedBlock,
                selectedRules: (migratedBlock.selectedRules || []).map((r) =>
                  r.category === "CUSTOM"
                    ? { ...r, parameters: { ...r.parameters, description: r.parameters?.description ?? desc } }
                    : r
                ),
              };
            }
            if (migratedBlock.customRuleDescription === undefined) {
              migratedBlock = { ...migratedBlock, customRuleDescription: "" };
            }
            migratedBlock = {
              ...migratedBlock,
              selectedRules: migrateSelectedRulesAdjustmentPartitions(migratedBlock.selectedRules),
            };
            return migratedBlock;
          });
        }
      }
    } catch (error) {
      console.error("Error loading saved rule blocks:", error);
    }
    // Default state
    return [
      {
        id: Date.now(),
        name: "",
        description: "",
        customRuleDescription: "", // CUSTOM rule type only → sent in configuration.parameters.description
        frequency: "RECURRING",
        start_date: "",
        end_date: "",
        cost_measurement: "UNBLENDED",
        status: "ACTIVE",
        selectedRules: [],
        allAccounts: true,
        selectedAccounts: [],
        selectedServicesEligible: [],
        selectedServicesIneligible: [],
        selectedRegionsEligible: [],
        selectedRegionsIneligible: [],
        scopeOperatorAccounts: "INCLUDE",
        scopeOperatorServices: "INCLUDE",
        scopeOperatorRegions: "INCLUDE",
        scopeOperatorRegionsIneligible: "INCLUDE",
        awsPartnerSupportDraftParameters: undefined,
        awsPartnerSupportMatrix: undefined,
      },
    ];
  };

  // Rule blocks state - array of rule configurations
  const [ruleBlocks, setRuleBlocks] = useState(getInitialRuleBlocks);

  // Save rule blocks to sessionStorage whenever they change
  useEffect(() => {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(ruleBlocks));
    } catch (error) {
      console.error("Error saving rule blocks to sessionStorage:", error);
    }
  }, [ruleBlocks, STORAGE_KEY]);

  // Default: Transfer / Tax enabled with Full amount when metadata loads (fresh load).
  const defaultTaxAppliedRef = useRef(false);
  useEffect(() => {
    if (defaultTaxAppliedRef.current) return;
    const categories = metadata?.categories;
    if (!Array.isArray(categories) || categories.length === 0) return;
    setRuleBlocks((prev) => {
      if (prev.length !== 1 || (prev[0].selectedRules?.length ?? 0) > 0) return prev;
      const transferTax = findTransferTaxRuleType(categories);
      if (!transferTax) return prev;
      const { taxCat, taxType } = transferTax;
      const defaultParams = buildDefaultRuleParametersFromTypeDef(taxType);
      defaultTaxAppliedRef.current = true;
      return [
        {
          ...prev[0],
          selectedRules: [{ category: taxCat.category, type: taxType.type, parameters: defaultParams }],
        },
      ];
    });
  }, [metadata?.categories]);

  // Service and Region metadata
  const [serviceCodes, setServiceCodes] = useState([]);
  const [ineligibleServiceCodes, setIneligibleServiceCodes] = useState([]);
  const [regions, setRegions] = useState([]);
  const [isFetchingServiceCodes, setIsFetchingServiceCodes] = useState(false);
  const [isFetchingIneligibleServiceCodes, setIsFetchingIneligibleServiceCodes] = useState(false);
  const [isFetchingRegions, setIsFetchingRegions] = useState(false);

  const ineligibleServiceCodesSet = useMemo(
    () => new Set(ineligibleServiceCodes),
    [ineligibleServiceCodes]
  );

  const eligibleServiceCodesFromApi = useMemo(
    () => getEligibleServiceCodesFromApi(serviceCodes, ineligibleServiceCodesSet),
    [serviceCodes, ineligibleServiceCodesSet]
  );

  const adjustmentBlocksWithServiceSelections = useMemo(
    () =>
      ruleBlocks.some((block) => {
        const selectedRules = block.selectedRules || [];
        const hasAdjustment = selectedRules.some((r) => r.category === "ADJUSTMENT");
        const nElig = (block.selectedServicesEligible || []).length;
        const nInelig = (block.selectedServicesIneligible || []).length;
        return hasAdjustment && (nElig > 0 || nInelig > 0);
      }),
    [ruleBlocks]
  );

  // Migrate legacy combined selectedServices/selectedRegions into partition-specific fields once metadata is available.
  // Also strip legacy keys from blocks that already use partitioned fields (e.g. reintroduced by an old category handler).
  useEffect(() => {
    setRuleBlocks((prev) => {
      let changed = false;
      const next = prev.map((block) => {
        const fullyPartitioned =
          block.selectedServicesEligible !== undefined && block.selectedServicesIneligible !== undefined;
        if (fullyPartitioned) {
          if (block.selectedServices !== undefined || block.selectedRegions !== undefined) {
            changed = true;
            const { selectedServices: _dropS, selectedRegions: _dropR, ...rest } = block;
            return rest;
          }
          return block;
        }
        changed = true;
        const legacySvc = block.selectedServices || [];
        const legacyReg = block.selectedRegions || [];
        const { eligible, ineligible } = partitionSelectedServicesForAwsUsage(
          Array.isArray(legacySvc) ? legacySvc : [],
          ineligibleServiceCodesSet
        );
        const { selectedServices: _drop, selectedRegions: _dropR, ...rest } = block;
        return {
          ...rest,
          selectedServicesEligible: eligible,
          selectedServicesIneligible: ineligible,
          selectedRegionsEligible: Array.isArray(legacyReg) ? legacyReg : [],
          selectedRegionsIneligible: [],
        };
      });
      return changed ? next : prev;
    });
  }, [ineligibleServiceCodesSet]);

  // Popover states for each rule block
  const [popoverStates, setPopoverStates] = useState({});

  // Snackbar state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);

  // Fetch metadata when component mounts (customers list for org label is loaded by useBillingCustomerOrgDisplay)
  useEffect(() => {
    if (customerOrgCode) {
      dispatch(
        getCustomerMetadata({
          customerOrgCode,
          params: {
            kind: "rule_definitions",
          },
        })
      );
      // Fetch service codes
      setIsFetchingServiceCodes(true);
      getCustomerMetadataApi(customerOrgCode, { kind: "service_codes", size: 200 })
        .then((response) => {
          const { status, ...responseData } = response;
          setServiceCodes(normalizeMetadataCodeList(responseData));
        })
        .catch((error) => {
          console.error("Error fetching service codes:", error);
        })
        .finally(() => {
          setIsFetchingServiceCodes(false);
        });

      setIsFetchingIneligibleServiceCodes(true);
      getCustomerMetadataApi(customerOrgCode, { kind: "ineligible_service_codes", size: 200 })
        .then((response) => {
          const { status, ...responseData } = response;
          setIneligibleServiceCodes(normalizeMetadataCodeList(responseData));
        })
        .catch((error) => {
          console.error("Error fetching ineligible service codes:", error);
        })
        .finally(() => {
          setIsFetchingIneligibleServiceCodes(false);
        });
      // Fetch regions
      setIsFetchingRegions(true);
      getCustomerMetadataApi(customerOrgCode, { kind: "regions" })
        .then((response) => {
          const { status, ...responseData } = response;
          if (responseData.values) {
            setRegions(responseData.values);
          }
          setIsFetchingRegions(false);
        })
        .catch((error) => {
          console.error("Error fetching regions:", error);
          setIsFetchingRegions(false);
        });
    }
  }, [customerOrgCode, dispatch]);

  // Add new rule block (each block has its own name, dates, accounts; can multi-select rule types)
  const handleAddRuleBlock = () => {
    if (ruleBlocks.length >= MAX_RULE_BLOCKS) {
      setSnackbar({
        open: true,
        message: `You can add at most ${MAX_RULE_BLOCKS} billing rules at once.`,
        severity: "warning",
      });
      return;
    }
    setRuleBlocks([
      ...ruleBlocks,
      {
        id: Date.now(),
        name: "",
        description: "",
        customRuleDescription: "",
        frequency: "RECURRING",
        start_date: "",
        end_date: "",
        cost_measurement: "UNBLENDED",
        status: "ACTIVE",
        selectedRules: [],
        allAccounts: true,
        selectedAccounts: [],
        selectedServicesEligible: [],
        selectedServicesIneligible: [],
        selectedRegionsEligible: [],
        selectedRegionsIneligible: [],
        scopeOperatorAccounts: "INCLUDE",
        scopeOperatorServices: "INCLUDE",
        scopeOperatorRegions: "INCLUDE",
        scopeOperatorRegionsIneligible: "INCLUDE",
        awsPartnerSupportDraftParameters: undefined,
        awsPartnerSupportMatrix: undefined,
      },
    ]);
  };

  // Remove rule block
  const handleRemoveRuleBlock = (blockId) => {
    if (ruleBlocks.length > 1) {
      setRuleBlocks(ruleBlocks.filter((block) => block.id !== blockId));
    }
  };

  // Update rule block field
  const updateRuleBlock = (blockId, field, value) => {
    setRuleBlocks(
      ruleBlocks.map((block) =>
        block.id === blockId ? { ...block, [field]: value } : block
      )
    );
  };

  // Update rule block array field (partitioned service/region lists)
  const updateRuleBlockArray = (blockId, field, newArray) => {
    setRuleBlocks(
      ruleBlocks.map((block) =>
        block.id === blockId ? { ...block, [field]: newArray } : block
      )
    );
  };


  // Flat list of all (category, type) pairs for multi-select UX
  const getAllCategoryTypePairs = () => {
    if (!metadata?.categories) return [];
    return metadata.categories.flatMap((cat) =>
      (cat.types || []).map((t) => ({
        category: cat.category,
        categoryLabel:
          cat.category === "ADJUSTMENT"
            ? "AWS Usage - Adjustments"
            : cat.category === "SUPPORT"
              ? "AWS Partner Led Support"
              : cat.label || cat.category,
        type: t.type,
        typeLabel: t.label || t.type,
        typeDescription: t.description,
        required_parameters: t.required_parameters || [],
        allowed_scopes: cat.allowed_scopes || [],
      }))
    );
  };

  // Group pairs by category for grouped UI. Exclude from ADJUSTMENT any type that is CUSTOM's type (e.g. FIXED_COST) so it only appears in Custom section.
  const getCategoryTypePairsByCategory = () => {
    const pairs = getAllCategoryTypePairs();
    const customTypeName = metadata?.categories?.find((c) => c.category === "CUSTOM")?.types?.[0]?.type ?? null;
    const byCategory = {};
    pairs.forEach((p) => {
      if (HIDE_TAXES_CATEGORY_IN_CREATE_UI && isTaxesStandaloneCategory(p.category)) return;
      if (!byCategory[p.category]) byCategory[p.category] = { label: p.categoryLabel, types: [] };
      // Don't show FIXED_COST (or CUSTOM's type) under ADJUSTMENT – it's only in the Custom section
      if (p.category === "ADJUSTMENT" && customTypeName && p.type === customTypeName) return;
      if (p.category === "ADJUSTMENT" && isDeprecatedAdjustmentRuleType(p.type)) return;
      byCategory[p.category].types.push(p);
    });
    return Object.entries(byCategory)
      .map(([category, { label, types }]) => ({ category, label, types }))
      .sort((a, b) => {
        if (a.category === "TRANSFER" && b.category !== "TRANSFER") return -1;
        if (b.category === "TRANSFER" && a.category !== "TRANSFER") return 1;
        return 0;
      });
  };

  // Get parameters for a (category, type) from block.selectedRules
  const getSelectedRule = (block, category, type, adjustmentPartition) => {
    const rules = block.selectedRules || [];
    if (category === "ADJUSTMENT" && adjustmentPartition) {
      return rules.find(
        (r) =>
          r.category === category &&
          r.type === type &&
          (r.adjustmentPartition || ADJUSTMENT_PARTITION_ELIGIBLE) === adjustmentPartition
      );
    }
    return rules.find((r) => r.category === category && r.type === type);
  };

  // Toggle a rule type on/off in a block
  const handleToggleRuleType = (blockId, category, type, enabled, adjustmentPartition) => {
    setRuleBlocks(
      ruleBlocks.map((block) => {
        if (block.id !== blockId) return block;
        let selectedRules = [...(block.selectedRules || [])];
        let nextMatrix = block.awsPartnerSupportMatrix;
        const part = category === "ADJUSTMENT" ? adjustmentPartition || ADJUSTMENT_PARTITION_ELIGIBLE : null;
        const idx = selectedRules.findIndex((r) => {
          if (r.category !== category || r.type !== type) return false;
          if (category === "ADJUSTMENT") {
            return (r.adjustmentPartition || ADJUSTMENT_PARTITION_ELIGIBLE) === part;
          }
          return true;
        });
        if (enabled) {
          if (idx >= 0) return block;
          if (category === "ADJUSTMENT") {
            selectedRules = selectedRules.filter(
              (r) =>
                !(r.category === "ADJUSTMENT" && (r.adjustmentPartition || ADJUSTMENT_PARTITION_ELIGIBLE) === part)
            );
          }
          if (category === SUPPORT_RULE_CATEGORY && type === AWS_PARTNER_SUPPORT_RULE_TYPE) {
            selectedRules = selectedRules.filter(
              (r) => !(r.category === SUPPORT_RULE_CATEGORY && r.type === AWS_PARTNER_SUPPORT_RULE_TYPE)
            );
          }
          const typeDef = metadata?.categories
            ?.find((c) => c.category === category)
            ?.types?.find((t) => t.type === type);
          let defaultParams =
            category === "EXCLUSION"
              ? { enabled: true }
              : category === SUPPORT_RULE_CATEGORY && type === AWS_PARTNER_SUPPORT_RULE_TYPE
                ? buildAwsPartnerSupportDefaultParameters("BUSINESS_SUPPORT")
                : isTaxesStandaloneCategory(category)
                  ? buildDefaultRuleParametersFromTypeDef(typeDef)
                  : {};
          if (category === SUPPORT_RULE_CATEGORY && type === AWS_PARTNER_SUPPORT_RULE_TYPE) {
            const matrixForEnable =
              nextMatrix?.byPlan
                ? nextMatrix
                : buildAwsPartnerSupportMatrixFromParameters(
                    block.awsPartnerSupportDraftParameters ||
                      buildAwsPartnerSupportDefaultParameters("BUSINESS_SUPPORT")
                  );
            defaultParams = getActivePlanParametersFromMatrix(matrixForEnable);
            nextMatrix = matrixForEnable;
          }
          const entry = { category, type, parameters: defaultParams };
          if (category === "ADJUSTMENT") entry.adjustmentPartition = part;
          selectedRules.push(entry);
        } else {
          if (idx < 0) return block;
          const removing = selectedRules[idx];
          if (
            removing?.category === SUPPORT_RULE_CATEGORY &&
            removing?.type === AWS_PARTNER_SUPPORT_RULE_TYPE
          ) {
            nextMatrix = block.awsPartnerSupportMatrix?.byPlan
              ? block.awsPartnerSupportMatrix
              : buildAwsPartnerSupportMatrixFromParameters(removing.parameters || {});
          }
          selectedRules.splice(idx, 1);
        }
        return {
          ...block,
          selectedRules,
          awsPartnerSupportMatrix: nextMatrix,
          awsPartnerSupportDraftParameters: undefined,
        };
      })
    );
  };

  const handleAwsPartnerSupportMatrixReplace = (blockId, nextMatrix) => {
    setRuleBlocks(
      ruleBlocks.map((block) => {
        if (block.id !== blockId) return block;
        const activeParams = getActivePlanParametersFromMatrix(nextMatrix);
        const hasPartner = (block.selectedRules || []).some(
          (r) => r.category === SUPPORT_RULE_CATEGORY && r.type === AWS_PARTNER_SUPPORT_RULE_TYPE
        );
        const selectedRules = hasPartner
          ? (block.selectedRules || []).map((r) =>
              r.category === SUPPORT_RULE_CATEGORY && r.type === AWS_PARTNER_SUPPORT_RULE_TYPE
                ? { ...r, parameters: activeParams }
                : r
            )
          : block.selectedRules;
        return {
          ...block,
          awsPartnerSupportMatrix: nextMatrix,
          selectedRules,
          awsPartnerSupportDraftParameters: undefined,
        };
      })
    );
  };

  // Update parameters for a specific (category, type) in a block
  const handleRuleTypeParameterChange = (blockId, category, type, key, value, adjustmentPartition) => {
    setRuleBlocks(
      ruleBlocks.map((block) => {
        if (block.id !== blockId) return block;
        const part = category === "ADJUSTMENT" ? adjustmentPartition || ADJUSTMENT_PARTITION_ELIGIBLE : null;
        const selectedRules = (block.selectedRules || []).map((r) => {
          const match =
            r.category === category && r.type === type &&
            (category !== "ADJUSTMENT" || (r.adjustmentPartition || ADJUSTMENT_PARTITION_ELIGIBLE) === part);
          return match ? { ...r, parameters: { ...r.parameters, [key]: value } } : r;
        });
        return { ...block, selectedRules };
      })
    );
  };

  // Get first CUSTOM type from metadata (e.g. FIXED_COST)
  const getCustomChargeType = () => {
    const customCat = metadata?.categories?.find((c) => c.category === "CUSTOM");
    const types = customCat?.types || [];
    return types[0] || null;
  };

  // Get all CUSTOM charge rows for a block (same category + type as first CUSTOM type)
  const getCustomChargeRows = (block) => {
    const customType = getCustomChargeType();
    if (!customType) return [];
    return (block.selectedRules || []).filter(
      (r) => r.category === "CUSTOM" && r.type === customType.type
    );
  };

  const countRulesTurnedOnInCategory = (block, category, types) => {
    if (category === "CUSTOM") {
      return getCustomChargeRows(block).filter((r) => r.parameters?.enabled !== false).length;
    }
    const categoryTypes = new Set((types || []).map((t) => t.type));
    return (block.selectedRules || []).filter((r) => {
      if (r.category !== category || !categoryTypes.has(r.type)) return false;
      if (category === "EXCLUSION" && r.parameters?.enabled === false) return false;
      return true;
    }).length;
  };

  const formatCategoryRulesOnSummary = (count) =>
    count === 1 ? "1 rule turned on" : `${count} rules turned on`;

  // Add a new custom charge row
  const handleAddCustomChargeRow = (blockId) => {
    const customType = getCustomChargeType();
    if (!customType) return;
    const amountKey = (customType.required_parameters || []).find(
      (p) => p.type === "number" && p.key !== "percent" && !(p.label || "").toLowerCase().includes("percent")
    )?.key || "amount";
    setRuleBlocks(
      ruleBlocks.map((block) => {
        if (block.id !== blockId) return block;
        const selectedRules = [...(block.selectedRules || [])];
        selectedRules.push({
          category: "CUSTOM",
          type: customType.type,
          parameters: { [amountKey]: "", description: "", enabled: false },
        });
        return { ...block, selectedRules };
      })
    );
  };

  // Update one custom charge row by index
  const handleCustomChargeChange = (blockId, rowIndex, key, value) => {
    const customType = getCustomChargeType();
    if (!customType) return;
    setRuleBlocks(
      ruleBlocks.map((block) => {
        if (block.id !== blockId) return block;
        const selectedRules = [...(block.selectedRules || [])];
        const customIndices = selectedRules
          .map((r, i) => (r.category === "CUSTOM" && r.type === customType.type ? i : -1))
          .filter((i) => i >= 0);
        const idx = customIndices[rowIndex];
        if (idx == null) return block;
        selectedRules[idx] = {
          ...selectedRules[idx],
          parameters: { ...selectedRules[idx].parameters, [key]: value },
        };
        return { ...block, selectedRules };
      })
    );
  };

  // Toggle custom charge row on/off. When off: reset fields to default; row stays visible.
  const handleCustomChargeToggle = (blockId, rowIndex, enabled) => {
    const customType = getCustomChargeType();
    if (!customType) return;
    const amountKey = (customType.required_parameters || []).find(
      (p) => p.type === "number" && p.key !== "percent" && !(p.label || "").toLowerCase().includes("percent")
    )?.key || "amount";
    setRuleBlocks(
      ruleBlocks.map((block) => {
        if (block.id !== blockId) return block;
        const selectedRules = [...(block.selectedRules || [])];
        const customIndices = selectedRules
          .map((r, i) => (r.category === "CUSTOM" && r.type === customType.type ? i : -1))
          .filter((i) => i >= 0);
        const idx = customIndices[rowIndex];
        if (idx == null) return block;
        selectedRules[idx] = {
          ...selectedRules[idx],
          parameters: {
            ...selectedRules[idx].parameters,
            enabled,
            ...(enabled ? {} : { [amountKey]: "", description: "" }),
          },
        };
        return { ...block, selectedRules };
      })
    );
  };

  // Remove a custom charge row (by index) – only for additional rows
  const handleRemoveCustomChargeRow = (blockId, rowIndex) => {
    const customType = getCustomChargeType();
    if (!customType) return;
    setRuleBlocks(
      ruleBlocks.map((block) => {
        if (block.id !== blockId) return block;
        const selectedRules = [...(block.selectedRules || [])];
        const customIndices = selectedRules
          .map((r, i) => (r.category === "CUSTOM" && r.type === customType.type ? i : -1))
          .filter((i) => i >= 0);
        const idx = customIndices[rowIndex];
        if (idx == null) return block;
        selectedRules.splice(idx, 1);
        return { ...block, selectedRules };
      })
    );
  };

  // Ensure each block has at least one Custom charge row by default once metadata is loaded.
  useEffect(() => {
    const customType = getCustomChargeType();
    if (!customType) return;
    const amountKey =
      (customType.required_parameters || []).find(
        (p) => p.type === "number" && p.key !== "percent" && !(p.label || "").toLowerCase().includes("percent")
      )?.key || "amount";
    setRuleBlocks((prevBlocks) => {
      let changed = false;
      const nextBlocks = prevBlocks.map((block) => {
        const customRows = (block.selectedRules || []).filter(
          (r) => r.category === "CUSTOM" && r.type === customType.type
        );
        if (customRows.length === 0) {
          changed = true;
          return {
            ...block,
            selectedRules: [
              ...(block.selectedRules || []),
              {
                category: "CUSTOM",
                type: customType.type,
                parameters: { [amountKey]: "", description: "", enabled: false },
              },
            ],
          };
        }
        return block;
      });
      return changed ? nextBlocks : prevBlocks;
    });
  }, [metadata?.categories]);

  // Set Full (100%) or Partial mode for a number param; when Full, set param value to 100
  const handleFullPartialChange = (blockId, category, type, paramKey, mode, adjustmentPartition) => {
    setRuleBlocks(
      ruleBlocks.map((block) => {
        if (block.id !== blockId) return block;
        const part = category === "ADJUSTMENT" ? adjustmentPartition || ADJUSTMENT_PARTITION_ELIGIBLE : null;
        const selectedRules = (block.selectedRules || []).map((r) => {
          const match =
            r.category === category && r.type === type &&
            (category !== "ADJUSTMENT" || (r.adjustmentPartition || ADJUSTMENT_PARTITION_ELIGIBLE) === part);
          if (!match) return r;
          const next = { ...r, parameters: { ...r.parameters, _fullPartial: mode } };
          if (mode === "full") next.parameters[paramKey] = 100;
          return next;
        });
        return { ...block, selectedRules };
      })
    );
  };

  // Clear saved state
  const clearSavedState = () => {
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch (error) {
      console.error("Error clearing saved state:", error);
    }
  };

  // Check if description is required for any selected rule type in block
  const isDescriptionRequiredForBlock = (block) => {
    const rules = block.selectedRules || [];
    return rules.some((r) => {
      const typeData = metadata?.categories
        ?.find((c) => c.category === r.category)
        ?.types?.find((t) => t.type === r.type);
      const params = typeData?.required_parameters || [];
      return params.some((p) => p.key === "description" && p.required);
    });
  };

  // Validate and proceed to review
  const handleProceedToReview = () => {
    for (const block of ruleBlocks) {
      const selectedRules = block.selectedRules || [];
      if (selectedRules.length === 0) {
        setSnackbar({
          open: true,
          message: "Please select at least one rule type for each rule configuration.",
          severity: "error",
        });
        return;
      }
      if (!block.name?.trim()) {
        setSnackbar({
          open: true,
          message: "Please enter a billing rule name for all rules.",
          severity: "error",
        });
        return;
      }
      // CUSTOM rule types require description (and amount) per enabled row
      const customRules = selectedRules.filter((r) => r.category === "CUSTOM" && r.parameters?.enabled !== false);
      for (const rule of customRules) {
        if (!(rule.parameters?.description ?? "").trim()) {
          setSnackbar({
            open: true,
            message: "Description is required for each custom charge.",
            severity: "error",
          });
          return;
        }
      }
      if (isDescriptionRequiredForBlock(block) && !block.description?.trim()) {
        setSnackbar({
          open: true,
          message: "Description is required for one or more selected rule types.",
          severity: "error",
        });
        return;
      }
      for (const rule of selectedRules) {
        // Skip disabled CUSTOM (e.g. Fixed Cost) rows – they don't require Amount or other params
        if (rule.category === "CUSTOM" && rule.parameters?.enabled === false) continue;

        if (rule.category === SUPPORT_RULE_CATEGORY && rule.type === AWS_PARTNER_SUPPORT_RULE_TYPE) {
          const v = validateAwsPartnerSupportParameters(rule.parameters);
          if (!v.ok) {
            setSnackbar({
              open: true,
              message: v.message,
              severity: "error",
            });
            return;
          }
          continue;
        }

        const typeData = metadata?.categories
          ?.find((c) => c.category === rule.category)
          ?.types?.find((t) => t.type === rule.type);
        const requiredParams = typeData?.required_parameters || [];
        for (const param of requiredParams) {
          if (param.required && param.key !== "description") {
            const val = rule.parameters?.[param.key];
            const isEmpty = val === undefined || val === null || val === "";
            const isAmountParam = (param.label || "").toLowerCase().includes("amount") || (param.key || "").toLowerCase() === "amount";
            const numVal = typeof val === "number" ? val : parseFloat(String(val).replace(/[^0-9.-]/g, ""));
            const isInvalidAmount = isAmountParam && !isEmpty && (Number.isNaN(numVal) || !Number.isFinite(numVal));
            if (isInvalidAmount) {
              setSnackbar({
                open: true,
                message: "Only numbers are accepted for amount fields.",
                severity: "error",
              });
              return;
            }
            if (isEmpty) {
              setSnackbar({
                open: true,
                message: `Please fill required parameter "${param.label}" for ${typeData?.label || rule.type}.`,
                severity: "error",
              });
              return;
            }
          }
        }
      }
      if (block.frequency === "RECURRING") {
        if (!block.start_date) {
          setSnackbar({
            open: true,
            message: "Please select start month for all recurring rules.",
            severity: "error",
          });
          return;
        }
        if (!block.end_date) {
          setSnackbar({
            open: true,
            message: "Please select end month for all recurring rules.",
            severity: "error",
          });
          return;
        }
      }
    }

    if (adjustmentBlocksWithServiceSelections && isFetchingIneligibleServiceCodes) {
      setSnackbar({
        open: true,
        message: "Still loading service options. Please wait a moment and try again.",
        severity: "warning",
      });
      return;
    }

    // Persist current state to sessionStorage so when user clicks Back from Review, frequency and all choices are restored
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(ruleBlocks));
    } catch (e) {
      console.error("Error saving rule blocks before review:", e);
    }

    // Navigate to review page with rule blocks data and metadata
    history.push(`/billing-management/customer/${customerOrgCode}/rules/review`, {
      ruleBlocks,
      customerOrgCode,
      metadata, // Pass metadata for category and type descriptions
    });
  };

  const confirmCancelAndLeave = () => {
    setCancelDialogOpen(false);
    clearSavedState();
    history.push(`/billing-management/customer/${customerOrgCode}/rules`);
  };

  // Render a single rule block
  const renderRuleBlock = (block, index) => {
    const selectedRules = block.selectedRules || [];
    const resolvePartnerMatrixForBlock = (selectedPartnerRule) => {
      if (block.awsPartnerSupportMatrix?.byPlan) return block.awsPartnerSupportMatrix;
      const base =
        selectedPartnerRule?.parameters ||
        block.awsPartnerSupportDraftParameters ||
        buildAwsPartnerSupportDefaultParameters("BUSINESS_SUPPORT");
      return buildAwsPartnerSupportMatrixFromParameters(base);
    };
    const renderRuleTypePairRow = (pair, adjustmentPartition) => {
      const selectedRule = getSelectedRule(block, pair.category, pair.type, adjustmentPartition);
      const enabled = !!selectedRule;
      const params = selectedRule?.parameters || {};

      if (pair.category === SUPPORT_RULE_CATEGORY && pair.type === AWS_PARTNER_SUPPORT_RULE_TYPE) {
        const requiredParameters = pair.required_parameters || [];
        const matrix = resolvePartnerMatrixForBlock(selectedRule);
        return (
          <Box
            key="SUPPORT-AWS_PARTNER_SUPPORT"
            sx={{
              p: 2,
              border: "1px solid #e2e8f0",
              borderRadius: 1.5,
              backgroundColor: enabled ? "#f8fafc" : "#ffffff",
              "&:hover": { borderColor: "#cbd5e1" },
            }}
          >
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, flexWrap: "wrap" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0, flex: "1 1 280px" }}>
                  <Switch
                    checked={enabled}
                    onChange={(e) =>
                      handleToggleRuleType(block.id, pair.category, pair.type, e.target.checked, adjustmentPartition)
                    }
                    sx={{
                      flexShrink: 0,
                      "& .MuiSwitch-switchBase.Mui-checked": { color: "#3b82f6" },
                      "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                        backgroundColor: "#3b82f6",
                        opacity: 0.5,
                      },
                    }}
                  />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontSize: "0.9375rem", fontWeight: 600, color: "#1e293b", lineHeight: 1.35 }}>
                      Select a Support Plan
                    </Typography>
                  </Box>
                </Box>
              </Box>
              <Box sx={{ width: "100%", minWidth: 0, alignSelf: "stretch" }}>
                <AwsPartnerSupportRuleFields
                  disabled={!enabled}
                  matrix={matrix}
                  requiredParameters={requiredParameters}
                  onMatrixChange={(next) => handleAwsPartnerSupportMatrixReplace(block.id, next)}
                />
              </Box>
            </Box>
          </Box>
        );
      }

      const isExclusion = pair.category === "EXCLUSION";
      const paramsToShow = pair.required_parameters.filter(
        (p) =>
          p.key !== "description" &&
          !(isExclusion && p.type === "boolean") &&
          p.type !== "boolean_map"
      );
      const booleanMapParams = pair.required_parameters.filter((p) => p.type === "boolean_map");
      const numberParams = paramsToShow.filter((p) => p.type === "number");
      const otherParams = paramsToShow.filter((p) => p.type !== "number");
      const hasValueBox = numberParams.length > 0;
      const showRightColumn = hasValueBox || otherParams.length > 0;
      const pairKey =
        pair.category === "ADJUSTMENT" && adjustmentPartition
          ? `${pair.category}-${pair.type}-${adjustmentPartition}`
          : `${pair.category}-${pair.type}`;
      return (
        <Box
          key={pairKey}
          sx={{
            p: 2,
            border: "1px solid #e2e8f0",
            borderRadius: 1.5,
            backgroundColor: enabled ? "#f0f9ff" : "#ffffff",
            "&:hover": { borderColor: "#cbd5e1" },
          }}
        >
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, flexWrap: "wrap" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0, flex: "1 1 280px" }}>
              <Switch
                checked={enabled}
                onChange={(e) =>
                  handleToggleRuleType(block.id, pair.category, pair.type, e.target.checked, adjustmentPartition)
                }
                sx={{
                  flexShrink: 0,
                  "& .MuiSwitch-switchBase.Mui-checked": { color: "#3b82f6" },
                  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: "#3b82f6", opacity: 0.5 },
                }}
              />
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: "0.875rem", fontWeight: 500, color: "#1e293b" }}>
                  {pair.typeLabel}
                </Typography>
                {pair.typeDescription && (
                  <Typography sx={{ fontSize: "0.8125rem", color: "#64748b", mt: 0.25 }}>
                    {pair.typeDescription}
                  </Typography>
                )}
              </Box>
            </Box>
            {showRightColumn && (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.25, alignItems: "center", minWidth: 280, justifyContent: "flex-end" }}>
                {numberParams.map((param) => {
                  const isPercent = param.key === "percent" || (param.label || "").toLowerCase().includes("percent");
                  if (isPercent) {
                    const isFull = params._fullPartial === "full";
                    const rawPercent = params[param.key];
                    const displayValue = isFull
                      ? 100
                      : rawPercent === "" || rawPercent == null
                        ? ""
                        : rawPercent;
                    return (
                      <Box key={param.key} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Button
                          size="small"
                          variant={isFull ? "contained" : "outlined"}
                          onClick={() =>
                            handleFullPartialChange(
                              block.id,
                              pair.category,
                              pair.type,
                              param.key,
                              "full",
                              adjustmentPartition
                            )
                          }
                          disabled={!enabled}
                          sx={{
                            minWidth: 88,
                            py: 0.75,
                            px: 1.5,
                            textTransform: "none",
                            fontSize: "0.8125rem",
                            fontWeight: 500,
                            borderColor: "#3b82f6",
                            color: isFull ? "#fff" : "#3b82f6",
                            borderRadius: 2,
                            "&:hover": { borderColor: "#2563eb", backgroundColor: isFull ? undefined : "rgba(59, 130, 246, 0.08)" },
                          }}
                        >
                          Full Amount
                        </Button>
                        <Button
                          size="small"
                          variant={!isFull ? "contained" : "outlined"}
                          onClick={() =>
                            handleFullPartialChange(
                              block.id,
                              pair.category,
                              pair.type,
                              param.key,
                              "partial",
                              adjustmentPartition
                            )
                          }
                          disabled={!enabled}
                          sx={{
                            minWidth: 88,
                            py: 0.75,
                            px: 1.5,
                            textTransform: "none",
                            fontSize: "0.8125rem",
                            fontWeight: 500,
                            borderColor: "#3b82f6",
                            color: !isFull ? "#fff" : "#3b82f6",
                            borderRadius: 2,
                            "&:hover": { borderColor: "#2563eb", backgroundColor: !isFull ? undefined : "rgba(59, 130, 246, 0.08)" },
                          }}
                        >
                          Partial Amount
                        </Button>
                        <TextField
                          size="small"
                          type="text"
                          inputMode="decimal"
                          value={displayValue}
                          onChange={(e) => {
                            const raw = String(e.target.value).replace(/[^0-9.]/g, "");
                            const parts = raw.split(".");
                            const normalized = parts.length > 1
                              ? `${parts[0]}.${parts.slice(1).join("").slice(0, 2)}`
                              : raw;
                            if (normalized === "") {
                              handleRuleTypeParameterChange(
                                block.id,
                                pair.category,
                                pair.type,
                                param.key,
                                "",
                                adjustmentPartition
                              );
                              return;
                            }
                            handleRuleTypeParameterChange(
                              block.id,
                              pair.category,
                              pair.type,
                              param.key,
                              normalized,
                              adjustmentPartition
                            );
                          }}
                          onBlur={(e) => {
                            const text = String(e.target.value || "").trim();
                            if (text === "") return;
                            const parsed = Number.parseFloat(text);
                            if (Number.isNaN(parsed)) {
                              handleRuleTypeParameterChange(
                                block.id,
                                pair.category,
                                pair.type,
                                param.key,
                                "",
                                adjustmentPartition
                              );
                              return;
                            }
                            const clamped = Math.min(99, Math.max(0.01, parsed));
                            handleRuleTypeParameterChange(
                              block.id,
                              pair.category,
                              pair.type,
                              param.key,
                              clamped,
                              adjustmentPartition
                            );
                          }}
                          disabled={!enabled || isFull}
                          inputProps={{ min: 0.01, max: 99, step: 0.01 }}
                          sx={{
                            width: 72,
                            minWidth: 72,
                            "& .MuiInputBase-input": { textAlign: "center", py: 0.75 },
                            "& .MuiOutlinedInput-root": { borderRadius: 2 },
                          }}
                        />
                        <Typography component="span" sx={{ fontSize: "0.875rem", color: "#64748b", fontWeight: 500 }}>
                          %
                        </Typography>
                      </Box>
                    );
                  }
                  const rawAmount = params[param.key];
                  const amountValue = rawAmount === "" || rawAmount == null || Number.isNaN(Number(rawAmount)) ? "" : rawAmount;
                  return (
                    <Box key={param.key} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <TextField
                        size="small"
                        type="text"
                        inputMode="decimal"
                        value={amountValue}
                        onChange={(e) => {
                          const raw = String(e.target.value).replace(/[^0-9.]/g, "");
                          const parts = raw.split(".");
                          const normalized = parts.length > 1
                            ? `${parts[0]}.${parts.slice(1).join("").slice(0, 2)}`
                            : raw;
                          if (normalized === "") {
                            handleRuleTypeParameterChange(
                              block.id,
                              pair.category,
                              pair.type,
                              param.key,
                              "",
                              adjustmentPartition
                            );
                            return;
                          }
                          handleRuleTypeParameterChange(
                            block.id,
                            pair.category,
                            pair.type,
                            param.key,
                            normalized,
                            adjustmentPartition
                          );
                        }}
                        disabled={!enabled}
                        placeholder={param.label || "Amount"}
                        inputProps={{ min: 0, step: 0.01 }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">$</InputAdornment>
                          ),
                        }}
                        sx={{
                          width: 180,
                          minWidth: 180,
                          "& .MuiInputBase-input": { textAlign: "center", py: 0.75 },
                          "& .MuiOutlinedInput-root": { borderRadius: 2 },
                        }}
                      />
                    </Box>
                  );
                })}
                {enabled &&
                  otherParams.map((param) =>
                    param.type === "boolean" ? (
                      <FormControlLabel
                        key={param.key}
                        control={
                          <Checkbox
                            size="small"
                            checked={params[param.key] || false}
                            onChange={(e) =>
                              handleRuleTypeParameterChange(
                                block.id,
                                pair.category,
                                pair.type,
                                param.key,
                                e.target.checked,
                                adjustmentPartition
                              )
                            }
                          />
                        }
                        label={<Typography sx={{ fontSize: "0.8125rem" }}>{param.label}</Typography>}
                      />
                    ) : (
                      <TextField
                        key={param.key}
                        size="small"
                        label={param.label}
                        type="text"
                        value={params[param.key] ?? ""}
                        onChange={(e) =>
                          handleRuleTypeParameterChange(
                            block.id,
                            pair.category,
                            pair.type,
                            param.key,
                            e.target.value,
                            adjustmentPartition
                          )
                        }
                        required={param.required}
                        sx={{ width: 120, "& .MuiInputBase-root": { borderRadius: 2 } }}
                      />
                    )
                  )}
              </Box>
            )}
          </Box>
          {booleanMapParams.map((param) => {
            const options = getAppliesToRuleTypeOptions(param);
            const mapValue = params[param.key];
            const selectedValues = getSelectedAppliesToRuleTypeValues(
              mapValue && typeof mapValue === "object"
                ? mapValue
                : buildDefaultAppliesToRuleTypesMap(param)
            );
            return (
              <Box
                key={param.key}
                sx={{
                  mt: 1.25,
                  pl: 7,
                  pr: 0.5,
                  display: "flex",
                  alignItems: "center",
                  gap: 1.25,
                  flexWrap: "wrap",
                }}
              >
                <Typography
                  sx={{
                    fontSize: "0.8125rem",
                    fontWeight: 500,
                    color: "#64748b",
                    flexShrink: 0,
                    minWidth: 68,
                  }}
                >
                  Applies to
                </Typography>
                <Box sx={{ flex: "1 1 200px", maxWidth: 380, minWidth: 180 }}>
                  <AppliesToRuleTypesSelect
                    id={`${pairKey}-${param.key}`}
                    options={options}
                    value={selectedValues}
                    disabled={!enabled}
                    onChange={(nextSelected) =>
                      handleRuleTypeParameterChange(
                        block.id,
                        pair.category,
                        pair.type,
                        param.key,
                        buildAppliesToRuleTypesMapFromSelection(nextSelected, param),
                        adjustmentPartition
                      )
                    }
                  />
                </Box>
              </Box>
            );
          })}
        </Box>
      );
    };

    return (
      <Card
        key={block.id}
        sx={{
          mb: 3,
          border: "1px solid #e2e8f0",
          borderRadius: 2,
          boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
        }}
      >
        <CardContent sx={{ p: 3 }}>
          {index === 0 && ruleBlocks.length === 1 && (
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, color: "#1e293b" }}>
                Rule Configuration
              </Typography>
            </Box>
          )}
          {ruleBlocks.length > 1 && (
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, color: "#1e293b" }}>
                Rule {index + 1}
              </Typography>
              <IconButton
                onClick={() => handleRemoveRuleBlock(block.id)}
                sx={{ color: "#ef4444" }}
                size="small"
              >
                <DeleteIcon />
              </IconButton>
            </Box>
          )}

          <Box>
            {/* Form Fields */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", lg: "row" },
                  gap: 1,
                  alignItems: "stretch",
                }}
              >
                {/* Frequency — title row + single control row: toggles | separator | effective period */}
                <Box
                  sx={{
                    ...PARTNER_LED_RULE_OUTER_SX,
                    flex: { lg: "1 1 0" },
                    minWidth: 0,
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25, flex: 1 }}>
                    <Typography sx={PARTNER_LED_RULE_PRIMARY_TITLE_SX}>Frequency</Typography>

                    <Box
                      sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        alignItems: "center",
                        columnGap: 1.25,
                        rowGap: 1,
                      }}
                    >
                      <ToggleButtonGroup
                        exclusive
                        size="small"
                        value={block.frequency}
                        onChange={(_, value) => {
                          if (value) updateRuleBlock(block.id, "frequency", value);
                        }}
                        sx={{
                          flexShrink: 0,
                          backgroundColor: "#f8fafc",
                          borderRadius: "8px",
                          border: "1px solid #e2e8f0",
                          p: 0.25,
                          "& .MuiToggleButtonGroup-grouped": {
                            border: 0,
                            borderRadius: "6px !important",
                            mx: 0.125,
                            px: 1.5,
                            py: 0.5,
                            fontSize: "0.8125rem",
                            fontWeight: 500,
                            color: "#64748b",
                            textTransform: "none",
                            "&.Mui-selected": {
                              backgroundColor: "#ffffff",
                              color: "#1e40af",
                              boxShadow: "0 1px 2px rgba(15, 23, 42, 0.08)",
                              "&:hover": { backgroundColor: "#ffffff" },
                            },
                            "&:hover": { backgroundColor: alpha("#3b82f6", 0.06) },
                          },
                        }}
                      >
                        {(metadata?.frequencies || []).map((freq) => (
                          <ToggleButton key={freq.frequency} value={freq.frequency}>
                            {freq.label}
                          </ToggleButton>
                        ))}
                      </ToggleButtonGroup>

                      <Divider
                        orientation="vertical"
                        flexItem
                        sx={{
                          borderColor: "#e2e8f0",
                          alignSelf: "center",
                          height: 28,
                        }}
                      />

                      {block.frequency === "RECURRING" ? (
                        <Box
                          sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            alignItems: "center",
                            gap: 1,
                            rowGap: 1,
                            minWidth: 0,
                          }}
                        >
                          <Typography
                            component="span"
                            sx={{
                              fontSize: "0.8125rem",
                              fontWeight: 600,
                              color: "#64748b",
                              whiteSpace: "nowrap",
                            }}
                          >
                            Effective Period
                          </Typography>
                          <Typography sx={{ fontSize: "0.75rem", color: "#94a3b8", flexShrink: 0 }}>From</Typography>
                          <Select
                            size="small"
                            displayEmpty
                            required
                            value={block.start_date ? parseInt(block.start_date.split("-")[1], 10) - 1 : ""}
                            onChange={(e) => {
                              const month = e.target.value;
                              const year = block.start_date
                                ? parseInt(block.start_date.split("-")[0], 10)
                                : Math.max(2025, new Date().getFullYear());
                              const monthStr = String(month + 1).padStart(2, "0");
                              updateRuleBlock(block.id, "start_date", `${year}-${monthStr}`);
                            }}
                            renderValue={(v) =>
                              v === "" || v === undefined ? (
                                <Typography component="span" sx={{ color: "#94a3b8", fontSize: "0.8125rem" }}>
                                  Month
                                </Typography>
                              ) : (
                                BILLING_MONTH_OPTIONS[v]
                              )
                            }
                            sx={freqMonthSelectSx}
                          >
                            {BILLING_MONTH_OPTIONS.map((month, index) => (
                              <MenuItem key={index} value={index} sx={{ fontSize: "0.8125rem" }}>
                                {month}
                              </MenuItem>
                            ))}
                          </Select>
                          <Select
                            size="small"
                            required
                            value={
                              block.start_date
                                ? parseInt(block.start_date.split("-")[0], 10)
                                : Math.max(2025, new Date().getFullYear())
                            }
                            onChange={(e) => {
                              const year = e.target.value;
                              const month = block.start_date
                                ? parseInt(block.start_date.split("-")[1], 10) - 1
                                : new Date().getMonth();
                              const monthStr = String(month + 1).padStart(2, "0");
                              updateRuleBlock(block.id, "start_date", `${year}-${monthStr}`);
                            }}
                            sx={freqYearSelectSx}
                          >
                            {Array.from({ length: new Date().getFullYear() + 11 - 2025 }, (_, i) => {
                              const year = 2025 + i;
                              return (
                                <MenuItem key={year} value={year} sx={{ fontSize: "0.8125rem" }}>
                                  {year}
                                </MenuItem>
                              );
                            })}
                          </Select>
                          <Typography sx={{ fontSize: "0.75rem", color: "#cbd5e1", px: 0.25, flexShrink: 0 }} aria-hidden>
                            —
                          </Typography>
                          <Typography sx={{ fontSize: "0.75rem", color: "#94a3b8", flexShrink: 0 }}>To</Typography>
                          <Select
                            size="small"
                            displayEmpty
                            required
                            value={block.end_date ? parseInt(block.end_date.split("-")[1], 10) - 1 : ""}
                            onChange={(e) => {
                              const month = e.target.value;
                              const year = block.end_date
                                ? parseInt(block.end_date.split("-")[0], 10)
                                : Math.max(2026, new Date().getFullYear());
                              const monthStr = String(month + 1).padStart(2, "0");
                              updateRuleBlock(block.id, "end_date", `${year}-${monthStr}`);
                            }}
                            renderValue={(v) =>
                              v === "" || v === undefined ? (
                                <Typography component="span" sx={{ color: "#94a3b8", fontSize: "0.8125rem" }}>
                                  Month
                                </Typography>
                              ) : (
                                BILLING_MONTH_OPTIONS[v]
                              )
                            }
                            sx={freqMonthSelectSx}
                          >
                            {BILLING_MONTH_OPTIONS.map((month, index) => (
                              <MenuItem key={index} value={index} sx={{ fontSize: "0.8125rem" }}>
                                {month}
                              </MenuItem>
                            ))}
                          </Select>
                          <Select
                            size="small"
                            required
                            value={
                              block.end_date
                                ? parseInt(block.end_date.split("-")[0], 10)
                                : Math.max(2026, new Date().getFullYear())
                            }
                            onChange={(e) => {
                              const year = e.target.value;
                              const month = block.end_date
                                ? parseInt(block.end_date.split("-")[1], 10) - 1
                                : new Date().getMonth();
                              const monthStr = String(month + 1).padStart(2, "0");
                              updateRuleBlock(block.id, "end_date", `${year}-${monthStr}`);
                            }}
                            sx={freqYearSelectSx}
                          >
                            {Array.from({ length: new Date().getFullYear() + 11 - 2026 }, (_, i) => {
                              const year = 2026 + i;
                              return (
                                <MenuItem key={year} value={year} sx={{ fontSize: "0.8125rem" }}>
                                  {year}
                                </MenuItem>
                              );
                            })}
                          </Select>
                        </Box>
                      ) : (
                        <Typography
                          sx={{
                            fontSize: "0.8125rem",
                            color: "#64748b",
                            fontStyle: "italic",
                            lineHeight: 1.45,
                          }}
                        >
                          Effective period is only used for recurring rules.
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Box>

                {/* Linked accounts — title + primary row (aligned with Frequency block height) */}
                <Box
                  sx={{
                    ...PARTNER_LED_RULE_OUTER_SX,
                    flex: { lg: "0 1 380px" },
                    minWidth: { lg: 280 },
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25, flex: 1 }}>
                    <Typography sx={PARTNER_LED_RULE_PRIMARY_TITLE_SX}>Linked accounts</Typography>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={block.allAccounts !== false}
                          onChange={(e) => {
                            const isChecked = e.target.checked;
                            setRuleBlocks(
                              ruleBlocks.map((b) =>
                                b.id === block.id
                                  ? {
                                      ...b,
                                      allAccounts: isChecked,
                                      selectedAccounts: isChecked ? [] : b.selectedAccounts,
                                    }
                                  : b
                              )
                            );
                          }}
                          size="small"
                        />
                      }
                      label={
                        <Box>
                          <Typography sx={{ fontSize: "0.875rem", fontWeight: 500, color: "#1e293b" }}>
                            All accounts
                          </Typography>
                          <Typography sx={{ fontSize: "0.8125rem", color: "#64748b", mt: 0.25 }}>
                            Apply this rule to all linked accounts
                          </Typography>
                        </Box>
                      }
                      sx={{ m: 0, alignItems: "flex-start", cursor: "pointer" }}
                    />

                    {block.allAccounts === false && (
                      <Box
                        sx={{
                          p: 1.25,
                          border: "1px solid #e2e8f0",
                          borderRadius: 1.5,
                          backgroundColor: "#f8fafc",
                          display: "flex",
                          flexDirection: "column",
                          gap: 1,
                        }}
                      >
                        <Box>
                          {(block.selectedAccounts || []).length > 0 ? (
                            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                              {(block.selectedAccounts || []).map((account) => (
                                <Chip
                                  key={account}
                                  label={account}
                                  size="small"
                                  onDelete={() => {
                                    const currentAccounts = block.selectedAccounts || [];
                                    const newAccounts = currentAccounts.filter((a) => a !== account);
                                    updateRuleBlockArray(block.id, "selectedAccounts", newAccounts);
                                    if (newAccounts.length === 0) {
                                      updateRuleBlock(block.id, "allAccounts", true);
                                    }
                                  }}
                                  sx={{
                                    fontSize: "0.75rem",
                                    height: 24,
                                    backgroundColor: alpha("#3b82f6", 0.1),
                                    color: "#3b82f6",
                                  }}
                                />
                              ))}
                            </Box>
                          ) : (
                            <Typography sx={{ fontSize: "0.8125rem", color: "#64748b", fontStyle: "italic" }}>
                              No specific accounts selected
                            </Typography>
                          )}
                        </Box>
                        <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1 }}>
                          <FormControl size="small" sx={{ minWidth: 96, width: 96, flexShrink: 0 }}>
                            <Select
                              value={block.scopeOperatorAccounts || "INCLUDE"}
                              onChange={(e) => updateRuleBlock(block.id, "scopeOperatorAccounts", e.target.value)}
                              displayEmpty
                              sx={{ ...freqSelectOutlined, fontSize: "0.8125rem" }}
                            >
                              <MenuItem value="INCLUDE">Include</MenuItem>
                              <MenuItem value="EXCLUDE">Exclude</MenuItem>
                            </Select>
                          </FormControl>
                          <Button
                            variant="outlined"
                            endIcon={<ArrowDropDownIcon />}
                            onClick={(e) => {
                              setPopoverStates({
                                ...popoverStates,
                                [`account-${block.id}`]: {
                                  open: true,
                                  anchorEl: e.currentTarget,
                                  searchTerm: "",
                                },
                              });
                            }}
                            disabled={block.allAccounts === true}
                            sx={{
                              textTransform: "none",
                              fontSize: "0.8125rem",
                              borderColor: "#e2e8f0",
                              color: "#475569",
                              minWidth: 72,
                              height: 36,
                            }}
                          >
                            Add
                          </Button>
                        </Box>
                      </Box>
                    )}
                  </Box>
                </Box>
              </Box>
              {/* Rule types - multi-select with toggle per type (reference UX) */}
              {isFetchingMetadata ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  {getCategoryTypePairsByCategory().map(({ category, label, types }) => {
                    const rulesOnCount = countRulesTurnedOnInCategory(block, category, types);
                    const shellSx = {
                      border: "1px solid #e2e8f0",
                      borderRadius: 2,
                      overflow: "hidden",
                      backgroundColor: "#ffffff",
                      boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
                    };
                    const headerRow = (
                      <Box sx={{ display: "flex", alignItems: "center", width: "100%", pr: 1, gap: 1.5 }}>
                        <Typography sx={{ fontSize: "0.9375rem", fontWeight: 600, color: "#1e293b" }}>
                          {category === "CUSTOM" ? "Custom" : label}
                        </Typography>
                        <Typography
                          sx={{
                            ml: "auto",
                            fontSize: "0.8125rem",
                            fontWeight: 500,
                            color: rulesOnCount > 0 ? "#3b82f6" : "#94a3b8",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {formatCategoryRulesOnSummary(rulesOnCount)}
                        </Typography>
                      </Box>
                    );
                    const inner = (
                        <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 1.5 }}>
                        {category === "CUSTOM" ? (
                          (() => {
                            const customType = getCustomChargeType();
                            const customRows = getCustomChargeRows(block);
                            if (!customType) return null;
                            const amountParam = (customType.required_parameters || []).find(
                              (p) => p.type === "number" && (p.key !== "percent" && !(p.label || "").toLowerCase().includes("percent"))
                            ) || { key: "amount", label: "Amount" };
                            return (
                              <>
                                {customRows.map((row, rowIndex) => {
                                  const params = row.parameters || {};
                                  const enabled = params.enabled !== false;
                                  const rawAmount = params[amountParam.key];
                                  const amountValue = rawAmount === "" || rawAmount == null || Number.isNaN(Number(rawAmount)) ? "" : rawAmount;
                                  return (
                                    <Box
                                      key={`custom-${block.id}-${rowIndex}`}
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        flexWrap: "nowrap",
                                        gap: 2,
                                        p: 1.5,
                                        border: "1px solid #e2e8f0",
                                        borderRadius: 1.5,
                                        backgroundColor: enabled ? "#f0f9ff" : "#f8fafc",
                                        "&:hover": { borderColor: "#cbd5e1" },
                                      }}
                                    >
                                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexShrink: 0 }}>
                                        <Switch
                                          checked={enabled}
                                          onChange={(e) => handleCustomChargeToggle(block.id, rowIndex, e.target.checked)}
                                          sx={{
                                            "& .MuiSwitch-switchBase.Mui-checked": { color: "#3b82f6" },
                                            "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: "#3b82f6", opacity: 0.5 },
                                          }}
                                        />
                                        <Box sx={{ minWidth: 0 }}>
                                          <Typography sx={{ fontSize: "0.875rem", fontWeight: 500, color: "#1e293b" }}>
                                            {customType.label || "Fixed Cost"}
                                          </Typography>
                                          <Typography sx={{ fontSize: "0.8125rem", color: "#64748b", mt: 0.25 }}>
                                            {customType.description || "Add a fixed amount to the bill"}
                                          </Typography>
                                        </Box>
                                      </Box>
                                      <TextField
                                        size="small"
                                        placeholder="Describe this custom billing rule"
                                        value={params.description ?? ""}
                                        onChange={(e) => handleCustomChargeChange(block.id, rowIndex, "description", e.target.value)}
                                        disabled={!enabled}
                                        sx={{
                                          flex: "1 1 0",
                                          minWidth: 0,
                                          "& .MuiInputBase-root": { borderRadius: 2 },
                                        }}
                                        title="Required. Sent in rule parameters. Up to 1,024 characters."
                                      />
                                      <TextField
                                        size="small"
                                        type="text"
                                        inputMode="decimal"
                                        value={amountValue}
                                        onChange={(e) => {
                                          const raw = String(e.target.value).replace(/[^0-9.]/g, "");
                                          const parts = raw.split(".");
                                          const normalized = parts.length > 1
                                            ? `${parts[0]}.${parts.slice(1).join("").slice(0, 2)}`
                                            : raw;
                                          if (normalized === "") {
                                            handleCustomChargeChange(block.id, rowIndex, amountParam.key, "");
                                            return;
                                          }
                                          handleCustomChargeChange(block.id, rowIndex, amountParam.key, normalized);
                                        }}
                                        placeholder={amountParam.label || "Amount"}
                                        inputProps={{ min: 0, step: 0.01 }}
                                        InputProps={{
                                          startAdornment: (
                                            <InputAdornment position="start">$</InputAdornment>
                                          ),
                                        }}
                                        disabled={!enabled}
                                        sx={{
                                          width: 140,
                                          flexShrink: 0,
                                          "& .MuiInputBase-input": { py: 0.75 },
                                          "& .MuiOutlinedInput-root": { borderRadius: 2 },
                                        }}
                                      />
                                      <IconButton
                                        size="small"
                                        onClick={() => handleAddCustomChargeRow(block.id)}
                                        sx={{
                                          flexShrink: 0,
                                          color: "#3b82f6",
                                          "&:hover": { backgroundColor: "rgba(59, 130, 246, 0.08)" },
                                        }}
                                        aria-label="Add another custom charge row"
                                      >
                                        <AddIcon fontSize="small" />
                                      </IconButton>
                                      {rowIndex >= 1 && (
                                        <IconButton
                                          size="small"
                                          onClick={() => handleRemoveCustomChargeRow(block.id, rowIndex)}
                                          sx={{
                                            color: "#64748b",
                                            "&:hover": { color: "#dc2626", backgroundColor: "rgba(220, 38, 38, 0.08)" },
                                          }}
                                          aria-label="Remove this fixed cost row"
                                        >
                                          <DeleteIcon fontSize="small" />
                                        </IconButton>
                                      )}
                                    </Box>
                                  );
                                })}
                              </>
                            );
                          })()
                        ) : category === "ADJUSTMENT" ? (
                          <>
                            <Accordion
                              defaultExpanded={false}
                              disableGutters
                              elevation={0}
                              sx={ADJUSTMENT_PARTITION_ACCORDION_SX}
                            >
                              <AccordionSummary
                                expandIcon={<ExpandMoreIcon sx={{ color: "#64748b" }} />}
                                sx={ADJUSTMENT_PARTITION_SUMMARY_SX}
                              >
                                <Typography sx={{ fontSize: "0.9375rem", fontWeight: 600, color: "#1e293b" }}>
                                  Eligible services
                                </Typography>
                              </AccordionSummary>
                              <AccordionDetails sx={{ p: 2, display: "flex", flexDirection: "column", gap: 1.5 }}>
                              {types.map((pair) => renderRuleTypePairRow(pair, ADJUSTMENT_PARTITION_ELIGIBLE))}
                              <Box
                                sx={{
                                  border: "1px solid #cbd5e1",
                                  borderRadius: 2,
                                  overflow: "hidden",
                                  backgroundColor: "#ffffff",
                                }}
                              >
                                <Box sx={ADJUSTMENT_FILTERS_BAR_SX}>
                                  <Typography
                                    sx={{
                                      fontSize: "0.8125rem",
                                      fontWeight: 700,
                                      color: "#475569",
                                      textTransform: "uppercase",
                                      letterSpacing: "0.06em",
                                    }}
                                  >
                                    Filters
                                  </Typography>
                                </Box>
                                <Box sx={{ p: 1.5, display: "flex", flexDirection: "column", gap: 1.5 }}>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 1,
                                      p: 1.5,
                                      border: "1px solid #e2e8f0",
                                      borderRadius: "8px",
                                      backgroundColor: "#fafafa",
                                      flexWrap: "wrap",
                                    }}
                                  >
                                    <CloudIcon sx={{ fontSize: 18, color: "#64748b" }} />
                                    <Box sx={{ flex: 1, minWidth: 200 }}>
                                      <Typography sx={{ fontSize: "0.875rem", fontWeight: 500, color: "#1e293b", mb: 0.5 }}>
                                        Service
                                      </Typography>
                                      {(block.selectedServicesEligible || []).length > 0 ? (
                                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.5 }}>
                                          {(block.selectedServicesEligible || []).map((service) => (
                                            <Chip
                                              key={service}
                                              label={service}
                                              size="small"
                                              onDelete={() => {
                                                updateRuleBlockArray(
                                                  block.id,
                                                  "selectedServicesEligible",
                                                  (block.selectedServicesEligible || []).filter((s) => s !== service)
                                                );
                                              }}
                                              sx={{
                                                fontSize: "0.75rem",
                                                height: 24,
                                                backgroundColor: alpha("#3b82f6", 0.1),
                                                color: "#3b82f6",
                                              }}
                                            />
                                          ))}
                                        </Box>
                                      ) : (
                                        <Typography sx={{ fontSize: "0.8125rem", color: "#64748b", fontStyle: "italic" }}>
                                          Applies to all eligible services when none are selected. Use Add to pick from the catalog.
                                        </Typography>
                                      )}
                                    </Box>
                                    <FormControl size="small" sx={{ minWidth: 100, alignSelf: "flex-start" }}>
                                      <Select
                                        value={block.scopeOperatorServices || "INCLUDE"}
                                        onChange={(e) => updateRuleBlock(block.id, "scopeOperatorServices", e.target.value)}
                                        displayEmpty
                                        sx={{ fontSize: "0.8125rem", height: 36 }}
                                      >
                                        <MenuItem value="INCLUDE">Include</MenuItem>
                                        <MenuItem value="EXCLUDE">Exclude</MenuItem>
                                      </Select>
                                    </FormControl>
                                    <Button
                                      variant="outlined"
                                      endIcon={<ArrowDropDownIcon />}
                                      onClick={(e) => {
                                        setPopoverStates({
                                          ...popoverStates,
                                          [`service-eligible-${block.id}`]: {
                                            open: true,
                                            anchorEl: e.currentTarget,
                                            searchTerm: "",
                                          },
                                        });
                                      }}
                                      disabled={isFetchingServiceCodes || eligibleServiceCodesFromApi.length === 0}
                                      sx={{
                                        textTransform: "none",
                                        fontSize: "0.8125rem",
                                        borderColor: "#e2e8f0",
                                        color: "#475569",
                                        minWidth: 80,
                                        alignSelf: "flex-start",
                                      }}
                                    >
                                      Add
                                    </Button>
                                  </Box>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 1,
                                      p: 1.5,
                                      border: "1px solid #e2e8f0",
                                      borderRadius: "8px",
                                      backgroundColor: "#fafafa",
                                      flexWrap: "wrap",
                                    }}
                                  >
                                    <PublicIcon sx={{ fontSize: 18, color: "#64748b" }} />
                                    <Box sx={{ flex: 1, minWidth: 200 }}>
                                      <Typography sx={{ fontSize: "0.875rem", fontWeight: 500, color: "#1e293b", mb: 0.5 }}>
                                        Region
                                      </Typography>
                                      {(block.selectedRegionsEligible || []).length > 0 ? (
                                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.5 }}>
                                          {(block.selectedRegionsEligible || []).map((region) => (
                                            <Chip
                                              key={region}
                                              label={region}
                                              size="small"
                                              onDelete={() => {
                                                updateRuleBlockArray(
                                                  block.id,
                                                  "selectedRegionsEligible",
                                                  (block.selectedRegionsEligible || []).filter((r) => r !== region)
                                                );
                                              }}
                                              sx={{
                                                fontSize: "0.75rem",
                                                height: 24,
                                                backgroundColor: alpha("#3b82f6", 0.1),
                                                color: "#3b82f6",
                                              }}
                                            />
                                          ))}
                                        </Box>
                                      ) : (
                                        <Typography sx={{ fontSize: "0.8125rem", color: "#64748b", fontStyle: "italic" }}>
                                          Applies to all regions when none are selected.
                                        </Typography>
                                      )}
                                    </Box>
                                    <FormControl size="small" sx={{ minWidth: 100 }}>
                                      <Select
                                        value={block.scopeOperatorRegions || "INCLUDE"}
                                        onChange={(e) => updateRuleBlock(block.id, "scopeOperatorRegions", e.target.value)}
                                        displayEmpty
                                        sx={{ fontSize: "0.8125rem", height: 36 }}
                                      >
                                        <MenuItem value="INCLUDE">Include</MenuItem>
                                        <MenuItem value="EXCLUDE">Exclude</MenuItem>
                                      </Select>
                                    </FormControl>
                                    <Button
                                      variant="outlined"
                                      endIcon={<ArrowDropDownIcon />}
                                      onClick={(e) => {
                                        setPopoverStates({
                                          ...popoverStates,
                                          [`region-eligible-${block.id}`]: {
                                            open: true,
                                            anchorEl: e.currentTarget,
                                            searchTerm: "",
                                          },
                                        });
                                      }}
                                      disabled={isFetchingRegions}
                                      sx={{
                                        textTransform: "none",
                                        fontSize: "0.8125rem",
                                        borderColor: "#e2e8f0",
                                        color: "#475569",
                                        minWidth: 80,
                                      }}
                                    >
                                      Add
                                    </Button>
                                  </Box>
                                </Box>
                              </Box>
                              </AccordionDetails>
                            </Accordion>

                            <Accordion
                              defaultExpanded={false}
                              disableGutters
                              elevation={0}
                              sx={{ ...ADJUSTMENT_PARTITION_ACCORDION_SX, mt: 1 }}
                            >
                              <AccordionSummary
                                expandIcon={<ExpandMoreIcon sx={{ color: "#64748b" }} />}
                                sx={ADJUSTMENT_PARTITION_SUMMARY_SX}
                              >
                                <Typography sx={{ fontSize: "0.9375rem", fontWeight: 600, color: "#1e293b" }}>
                                  Ineligible services
                                </Typography>
                              </AccordionSummary>
                              <AccordionDetails sx={{ p: 2, display: "flex", flexDirection: "column", gap: 1.5 }}>
                              {types.map((pair) => renderRuleTypePairRow(pair, ADJUSTMENT_PARTITION_INELIGIBLE))}
                              <Box
                                sx={{
                                  border: "1px solid #cbd5e1",
                                  borderRadius: 2,
                                  overflow: "hidden",
                                  backgroundColor: "#ffffff",
                                }}
                              >
                                <Box sx={ADJUSTMENT_FILTERS_BAR_SX}>
                                  <Typography
                                    sx={{
                                      fontSize: "0.8125rem",
                                      fontWeight: 700,
                                      color: "#475569",
                                      textTransform: "uppercase",
                                      letterSpacing: "0.06em",
                                    }}
                                  >
                                    Filters
                                  </Typography>
                                </Box>
                                <Box sx={{ p: 1.5, display: "flex", flexDirection: "column", gap: 1.5 }}>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 1,
                                      p: 1.5,
                                      border: "1px solid #e2e8f0",
                                      borderRadius: "8px",
                                      backgroundColor: "#fafafa",
                                      flexWrap: "wrap",
                                    }}
                                  >
                                    <CloudIcon sx={{ fontSize: 18, color: "#64748b" }} />
                                    <Box sx={{ flex: 1, minWidth: 200 }}>
                                      <Typography sx={{ fontSize: "0.875rem", fontWeight: 500, color: "#1e293b", mb: 0.5 }}>
                                        Service
                                      </Typography>
                                      {(block.selectedServicesIneligible || []).length > 0 ? (
                                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.5 }}>
                                          {(block.selectedServicesIneligible || []).map((service) => (
                                            <Chip
                                              key={service}
                                              label={service}
                                              size="small"
                                              onDelete={() => {
                                                updateRuleBlockArray(
                                                  block.id,
                                                  "selectedServicesIneligible",
                                                  (block.selectedServicesIneligible || []).filter((s) => s !== service)
                                                );
                                              }}
                                              sx={{
                                                fontSize: "0.75rem",
                                                height: 24,
                                                backgroundColor: alpha("#3b82f6", 0.1),
                                                color: "#3b82f6",
                                              }}
                                            />
                                          ))}
                                        </Box>
                                      ) : (
                                        <Typography sx={{ fontSize: "0.8125rem", color: "#64748b", fontStyle: "italic" }}>
                                          Applies to all Ineligible services when none are selected. Use Add to pick from the ineligible list.
                                        </Typography>
                                      )}
                                    </Box>
                                    <Button
                                      variant="outlined"
                                      endIcon={<ArrowDropDownIcon />}
                                      onClick={(e) => {
                                        setPopoverStates({
                                          ...popoverStates,
                                          [`service-ineligible-${block.id}`]: {
                                            open: true,
                                            anchorEl: e.currentTarget,
                                            searchTerm: "",
                                          },
                                        });
                                      }}
                                      disabled={
                                        isFetchingIneligibleServiceCodes || ineligibleServiceCodes.length === 0
                                      }
                                      sx={{
                                        textTransform: "none",
                                        fontSize: "0.8125rem",
                                        borderColor: "#e2e8f0",
                                        color: "#475569",
                                        minWidth: 80,
                                        alignSelf: "flex-start",
                                      }}
                                    >
                                      Add
                                    </Button>
                                  </Box>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 1,
                                      p: 1.5,
                                      border: "1px solid #e2e8f0",
                                      borderRadius: "8px",
                                      backgroundColor: "#fafafa",
                                      flexWrap: "wrap",
                                    }}
                                  >
                                    <PublicIcon sx={{ fontSize: 18, color: "#64748b" }} />
                                    <Box sx={{ flex: 1, minWidth: 200 }}>
                                      <Typography sx={{ fontSize: "0.875rem", fontWeight: 500, color: "#1e293b", mb: 0.5 }}>
                                        Region
                                      </Typography>
                                      {(block.selectedRegionsIneligible || []).length > 0 ? (
                                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.5 }}>
                                          {(block.selectedRegionsIneligible || []).map((region) => (
                                            <Chip
                                              key={region}
                                              label={region}
                                              size="small"
                                              onDelete={() => {
                                                updateRuleBlockArray(
                                                  block.id,
                                                  "selectedRegionsIneligible",
                                                  (block.selectedRegionsIneligible || []).filter((r) => r !== region)
                                                );
                                              }}
                                              sx={{
                                                fontSize: "0.75rem",
                                                height: 24,
                                                backgroundColor: alpha("#3b82f6", 0.1),
                                                color: "#3b82f6",
                                              }}
                                            />
                                          ))}
                                        </Box>
                                      ) : (
                                        <Typography sx={{ fontSize: "0.8125rem", color: "#64748b", fontStyle: "italic" }}>
                                          Applies to all regions when none are selected.
                                        </Typography>
                                      )}
                                    </Box>
                                    <FormControl size="small" sx={{ minWidth: 100 }}>
                                      <Select
                                        value={block.scopeOperatorRegionsIneligible || "INCLUDE"}
                                        onChange={(e) =>
                                          updateRuleBlock(block.id, "scopeOperatorRegionsIneligible", e.target.value)
                                        }
                                        displayEmpty
                                        sx={{ fontSize: "0.8125rem", height: 36 }}
                                      >
                                        <MenuItem value="INCLUDE">Include</MenuItem>
                                        <MenuItem value="EXCLUDE">Exclude</MenuItem>
                                      </Select>
                                    </FormControl>
                                    <Button
                                      variant="outlined"
                                      endIcon={<ArrowDropDownIcon />}
                                      onClick={(e) => {
                                        setPopoverStates({
                                          ...popoverStates,
                                          [`region-ineligible-${block.id}`]: {
                                            open: true,
                                            anchorEl: e.currentTarget,
                                            searchTerm: "",
                                          },
                                        });
                                      }}
                                      disabled={isFetchingRegions}
                                      sx={{
                                        textTransform: "none",
                                        fontSize: "0.8125rem",
                                        borderColor: "#e2e8f0",
                                        color: "#475569",
                                        minWidth: 80,
                                      }}
                                    >
                                      Add
                                    </Button>
                                  </Box>
                                </Box>
                              </Box>
                              </AccordionDetails>
                            </Accordion>
                          </>
                        ) : (
                          types.map((pair) => renderRuleTypePairRow(pair))
                        )}
                        </Box>
                    );
                    const useAccordionForCategory =
                      types.length > 1 ||
                      category === SUPPORT_RULE_CATEGORY ||
                      category === "CUSTOM" ||
                      isTaxesStandaloneCategory(category);
                    if (!useAccordionForCategory) {
                      return (
                        <Box key={category} sx={shellSx}>
                          <Box
                            sx={{
                              px: 2,
                              py: 1.25,
                              backgroundColor: "#f8fafc",
                              borderBottom: "1px solid #e2e8f0",
                            }}
                          >
                            {headerRow}
                          </Box>
                          {inner}
                        </Box>
                      );
                    }
                    return (
                      <Accordion
                        key={category}
                        defaultExpanded={false}
                        disableGutters
                        elevation={0}
                        sx={{
                          ...shellSx,
                          "&:before": { display: "none" },
                        }}
                      >
                        <AccordionSummary
                          expandIcon={<ExpandMoreIcon sx={{ color: "#64748b" }} />}
                          sx={{
                            px: 2,
                            minHeight: 48,
                            backgroundColor: "#f8fafc",
                            borderBottom: "1px solid #e2e8f0",
                            "& .MuiAccordionSummary-content": { my: 1, alignItems: "center" },
                          }}
                        >
                          {headerRow}
                        </AccordionSummary>
                        <AccordionDetails sx={{ p: 0 }}>{inner}</AccordionDetails>
                      </Accordion>
                    );
                  })}
                </Box>
              )}

              {/* Rule Name */}
              <TextField
                label="Billing Rule Name"
                value={block.name}
                onChange={(e) => updateRuleBlock(block.id, "name", e.target.value)}
                required
                fullWidth
                size="small"
                helperText="The billing rule name can have up to 128 characters. Blank spaces and special characters are not valid."
                sx={{ "& .MuiInputBase-root": { borderRadius: 2 } }}
              />

              {/* Description - Required if any selected rule type needs it */}
              <TextField
                label={isDescriptionRequiredForBlock(block) ? "Description" : "Description - optional"}
                value={block.description}
                onChange={(e) => updateRuleBlock(block.id, "description", e.target.value)}
                required={isDescriptionRequiredForBlock(block)}
                fullWidth
                multiline
                rows={3}
                size="small"
                helperText="The billing rule description can have up to 1,024 characters."
                sx={{ "& .MuiInputBase-root": { borderRadius: 2 } }}
              />

            </Box>

          </Box>
        </CardContent>
      </Card>
    );
  };


  return (
    <Box sx={{ p: 3, backgroundColor: "#f1f5f9", minHeight: "100vh", pb: 8, mb: "3rem" }}>
      {/* Breadcrumbs with Back Button */}
      <Box sx={{ mb: 2.5, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Breadcrumbs
          separator={
            <Box
              component="span"
              sx={{
                color: "#cbd5e1",
                fontSize: "0.875rem",
                mx: 0.5,
                display: "flex",
                alignItems: "center",
              }}
            >
              /
            </Box>
          }
          aria-label="breadcrumb"
          sx={{
            "& .MuiBreadcrumbs-ol": {
              flexWrap: "nowrap",
            },
          }}
        >
          <Link
            component="button"
            variant="body2"
            onClick={() => history.push("/billing-management")}
            sx={{
              color: "#64748b",
              textDecoration: "none",
              fontSize: "0.875rem",
              fontWeight: 400,
              transition: "color 0.2s ease",
              "&:hover": {
                color: "#1e293b",
              },
              cursor: "pointer",
              px: 0.5,
            }}
          >
            Billing Management
          </Link>
          <Link
            component="button"
            variant="body2"
            onClick={() => history.push(`/billing-management/customer/${customerOrgCode}/rules`)}
            sx={{
              color: "#64748b",
              textDecoration: "none",
              fontSize: "0.875rem",
              fontWeight: 400,
              transition: "color 0.2s ease",
              "&:hover": {
                color: "#1e293b",
              },
              cursor: "pointer",
              px: 0.5,
            }}
          >
            Billing Rules{orgDisplayName ? ` (${orgDisplayName})` : ""}
          </Link>
          <Typography
            variant="body2"
            sx={{
              color: "#3b82f6",
              fontSize: "0.875rem",
              fontWeight: 600,
              px: 0.5,
            }}
          >
            Create Billing {ruleBlocks.length > 1 ? "Rules" : "Rule"}
          </Typography>
        </Breadcrumbs>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => setCancelDialogOpen(true)}
          sx={{
            textTransform: "none",
            color: "#64748b",
            fontSize: "0.875rem",
            fontWeight: 500,
            "&:hover": {
              backgroundColor: alpha("#3b82f6", 0.1),
              color: "#3b82f6",
            },
          }}
        >
          Back
        </Button>
      </Box>

      {/* Rule Blocks */}
      {ruleBlocks.map((block, index) => renderRuleBlock(block, index))}

      {/* Add Rule Button and Action Buttons */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 4, mt: 2 }}>
        <Button
          startIcon={<AddIcon />}
          onClick={handleAddRuleBlock}
          variant="outlined"
          disabled={ruleBlocks.length >= MAX_RULE_BLOCKS}
          sx={{
            textTransform: "none",
            borderColor: "#3b82f6",
            color: "#3b82f6",
            "&:hover": {
              borderColor: "#2563eb",
              backgroundColor: alpha("#3b82f6", 0.05),
            },
          }}
        >
          Add billing rule
        </Button>

        <Box sx={{ display: "flex", gap: 2 }}>
          <Button
            onClick={() => setCancelDialogOpen(true)}
            sx={{
              textTransform: "none",
              color: "#475569",
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleProceedToReview}
            variant="contained"
            disabled={
              adjustmentBlocksWithServiceSelections && isFetchingIneligibleServiceCodes
            }
            sx={{
              textTransform: "none",
              backgroundColor: "#3b82f6",
              "&:hover": {
                backgroundColor: "#2563eb",
              },
            }}
          >
            Review billing rules
          </Button>
        </Box>
      </Box>

      {/* Service Popovers (eligible = catalog minus ineligible list; ineligible = ineligible_service_codes only) */}
      {Object.keys(popoverStates).map((key) => {
        const m = key.match(/^service-(eligible|ineligible)-(\d+)$/);
        if (!m) return null;
        const partition = m[1];
        const blockId = parseInt(m[2], 10);
        const state = popoverStates[key];
        const block = ruleBlocks.find((b) => b.id === blockId);
        if (!block || !state) return null;

        const eligibleSelected = block.selectedServicesEligible || [];
        const ineligibleSelected = block.selectedServicesIneligible || [];
        const q = (state.searchTerm || "").toLowerCase();
        const matchSearch = (service) => !q || String(service).toLowerCase().includes(q);

        const eligibleOptionsFiltered = eligibleServiceCodesFromApi.filter(matchSearch);
        const ineligibleOptionsFiltered = ineligibleServiceCodes.filter(matchSearch);

        const isEligiblePopover = partition === "eligible";
        const selectedInPopover = isEligiblePopover ? eligibleSelected : ineligibleSelected;
        const serviceArrayField = isEligiblePopover ? "selectedServicesEligible" : "selectedServicesIneligible";

        const allInPartitionSelected = isEligiblePopover
          ? eligibleServiceCodesFromApi.length > 0 &&
            eligibleServiceCodesFromApi.every((s) => selectedInPopover.includes(s))
          : ineligibleServiceCodes.length > 0 &&
            ineligibleServiceCodes.every((s) => selectedInPopover.includes(s));
        const partitionSelectionEmpty = selectedInPopover.length === 0;

        const onToggleService = (service, checked) => {
          if (checked) {
            if (!selectedInPopover.includes(service)) {
              updateRuleBlockArray(block.id, serviceArrayField, [...selectedInPopover, service]);
            }
          } else {
            updateRuleBlockArray(
              block.id,
              serviceArrayField,
              selectedInPopover.filter((s) => s !== service)
            );
          }
        };

        return (
          <Popover
            key={key}
            open={state.open}
            anchorEl={state.anchorEl}
            onClose={() => {
              setPopoverStates({
                ...popoverStates,
                [key]: { ...state, open: false },
              });
            }}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            PaperProps={{
              sx: {
                width: 300,
                maxHeight: 400,
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
              },
            }}
          >
            <Box
              sx={{
                p: 1.5,
                minWidth: 300,
                maxHeight: 400,
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
                boxSizing: "border-box",
                flex: "1 1 auto",
                minHeight: 0,
              }}
            >
              <TextField
                size="small"
                placeholder="Search services..."
                value={state.searchTerm || ""}
                onChange={(e) => {
                  setPopoverStates({
                    ...popoverStates,
                    [key]: { ...state, searchTerm: e.target.value },
                  });
                }}
                fullWidth
                sx={{ mb: 1, flexShrink: 0 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ fontSize: 18, color: "#9ca3af" }} />
                    </InputAdornment>
                  ),
                }}
              />
              <Box
                sx={{
                  display: "flex",
                  gap: 2,
                  mb: 1,
                  pb: 1,
                  borderBottom: "1px solid #e5e7eb",
                  flexShrink: 0,
                }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={allInPartitionSelected}
                      onChange={() => {
                        if (!allInPartitionSelected) {
                          if (isEligiblePopover) {
                            updateRuleBlockArray(
                              block.id,
                              "selectedServicesEligible",
                              [...eligibleServiceCodesFromApi]
                            );
                          } else {
                            updateRuleBlockArray(
                              block.id,
                              "selectedServicesIneligible",
                              [...ineligibleServiceCodes]
                            );
                          }
                        }
                      }}
                    />
                  }
                  label={<Typography sx={{ fontSize: "0.8125rem" }}>Select all</Typography>}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={partitionSelectionEmpty}
                      onChange={() => {
                        if (!partitionSelectionEmpty) {
                          updateRuleBlockArray(block.id, serviceArrayField, []);
                        }
                      }}
                    />
                  }
                  label={<Typography sx={{ fontSize: "0.8125rem" }}>Clear</Typography>}
                />
              </Box>
              <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
                {isEligiblePopover
                  ? eligibleOptionsFiltered.map((service) => (
                      <FormControlLabel
                        key={service}
                        control={
                          <Checkbox
                            size="small"
                            checked={selectedInPopover.includes(service)}
                            onChange={(e) => onToggleService(service, e.target.checked)}
                          />
                        }
                        label={service}
                        sx={{ display: "flex", width: "100%", m: 0, py: 0.5 }}
                      />
                    ))
                  : ineligibleOptionsFiltered.map((service) => (
                      <FormControlLabel
                        key={service}
                        control={
                          <Checkbox
                            size="small"
                            checked={selectedInPopover.includes(service)}
                            onChange={(e) => onToggleService(service, e.target.checked)}
                          />
                        }
                        label={service}
                        sx={{ display: "flex", width: "100%", m: 0, py: 0.5 }}
                      />
                    ))}
              </Box>
            </Box>
          </Popover>
        );
      })}

      {/* Region Popover (eligible vs ineligible adjustment partitions) */}
      {Object.keys(popoverStates).map((key) => {
        if (!key.startsWith("region-")) return null;
        let blockId;
        let regionField = "selectedRegionsEligible";
        if (key.startsWith("region-eligible-")) {
          blockId = parseInt(key.slice("region-eligible-".length), 10);
        } else if (key.startsWith("region-ineligible-")) {
          blockId = parseInt(key.slice("region-ineligible-".length), 10);
          regionField = "selectedRegionsIneligible";
        } else {
          const parts = key.split("-");
          if (parts.length < 2) return null;
          blockId = parseInt(parts[1], 10);
        }
        const state = popoverStates[key];
        const block = ruleBlocks.find((b) => b.id === blockId);
        if (!block || !state) return null;
        const selectedRegionsList = block[regionField] || [];

        return (
          <Popover
            key={key}
            open={state.open}
            anchorEl={state.anchorEl}
            onClose={() => {
              setPopoverStates({
                ...popoverStates,
                [key]: { ...state, open: false },
              });
            }}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            PaperProps={{
              sx: {
                width: 300,
                maxHeight: 400,
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
              },
            }}
          >
            <Box
              sx={{
                p: 1.5,
                minWidth: 300,
                maxHeight: 400,
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
                boxSizing: "border-box",
                flex: "1 1 auto",
                minHeight: 0,
              }}
            >
              <TextField
                size="small"
                placeholder="Search regions..."
                value={state.searchTerm || ""}
                onChange={(e) => {
                  setPopoverStates({
                    ...popoverStates,
                    [key]: { ...state, searchTerm: e.target.value },
                  });
                }}
                fullWidth
                sx={{ mb: 1, flexShrink: 0 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ fontSize: 18, color: "#9ca3af" }} />
                    </InputAdornment>
                  ),
                }}
              />
              <Box
                sx={{
                  display: "flex",
                  gap: 2,
                  mb: 1,
                  pb: 1,
                  borderBottom: "1px solid #e5e7eb",
                  flexShrink: 0,
                }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={
                        selectedRegionsList.length === regions.length && regions.length > 0
                      }
                      onChange={() => {
                        if (selectedRegionsList.length < regions.length) {
                          updateRuleBlockArray(block.id, regionField, [...regions]);
                        }
                      }}
                    />
                  }
                  label={<Typography sx={{ fontSize: "0.8125rem" }}>Select all</Typography>}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={selectedRegionsList.length === 0}
                      onChange={() => {
                        if (selectedRegionsList.length > 0) {
                          updateRuleBlockArray(block.id, regionField, []);
                        }
                      }}
                    />
                  }
                  label={<Typography sx={{ fontSize: "0.8125rem" }}>Clear</Typography>}
                />
              </Box>
              <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
                {regions
                  .filter((region) =>
                    state.searchTerm
                      ? String(region).toLowerCase().includes(state.searchTerm.toLowerCase())
                      : true
                  )
                  .map((region) => (
                    <FormControlLabel
                      key={region}
                      control={
                        <Checkbox
                          size="small"
                          checked={selectedRegionsList.includes(region)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              updateRuleBlockArray(block.id, regionField, [
                                ...selectedRegionsList,
                                region,
                              ]);
                            } else {
                              updateRuleBlockArray(
                                block.id,
                                regionField,
                                selectedRegionsList.filter((r) => r !== region)
                              );
                            }
                          }}
                        />
                      }
                      label={region}
                      sx={{ display: "flex", width: "100%", m: 0, py: 0.5 }}
                    />
                  ))}
              </Box>
            </Box>
          </Popover>
        );
      })}

      {/* Account Popover */}
      {Object.keys(popoverStates).map((key) => {
        if (!key.startsWith("account-")) return null;
        const blockId = parseInt(key.split("-")[1]);
        const state = popoverStates[key];
        const block = ruleBlocks.find((b) => b.id === blockId);
        if (!block || !state) return null;

        return (
          <Popover
            key={key}
            open={state.open}
            anchorEl={state.anchorEl}
            onClose={() => {
              setPopoverStates({
                ...popoverStates,
                [key]: { ...state, open: false },
              });
            }}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
          >
            <Box sx={{ p: 1.5, minWidth: 300, maxHeight: 400 }}>
              <TextField
                size="small"
                placeholder="Search accounts..."
                value={state.searchTerm || ""}
                onChange={(e) => {
                  setPopoverStates({
                    ...popoverStates,
                    [key]: { ...state, searchTerm: e.target.value },
                  });
                }}
                fullWidth
                sx={{ mb: 1 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ fontSize: 18, color: "#9ca3af" }} />
                    </InputAdornment>
                  ),
                }}
              />
              <Box sx={{ maxHeight: 300, overflow: "auto" }}>
                {metadata?.accounts
                  ?.filter((account) =>
                    state.searchTerm
                      ? account.toLowerCase().includes(state.searchTerm.toLowerCase())
                      : true
                  )
                  .map((account) => (
                    <MenuItem
                      key={account}
                      onClick={() => {
                        const currentAccounts = block.selectedAccounts || [];
                        if (!currentAccounts.includes(account)) {
                          // If selecting a specific account, uncheck "All Accounts"
                          updateRuleBlock(block.id, "allAccounts", false);
                          updateRuleBlockArray(block.id, "selectedAccounts", [...currentAccounts, account]);
                        }
                      }}
                      disabled={(block.selectedAccounts || []).includes(account) || block.allAccounts === true}
                      sx={{
                        fontSize: "0.875rem",
                        py: 0.75,
                        backgroundColor: (block.selectedAccounts || []).includes(account) ? alpha("#3b82f6", 0.1) : "transparent",
                      }}
                    >
                      {account}
                    </MenuItem>
                  ))}
              </Box>
            </Box>
          </Popover>
        );
      })}

      <Dialog
        open={cancelDialogOpen}
        onClose={() => setCancelDialogOpen(false)}
        aria-labelledby="create-rule-cancel-title"
      >
        <DialogTitle id="create-rule-cancel-title" sx={{ fontWeight: 600, fontSize: "1rem" }}>
          Leave this page?
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: "#475569", fontSize: "0.875rem" }}>
            The billing rule you're working on isn't saved yet. If you go back now, you'll lose what you've entered. You can always start a new rule later from the Billing Rules page.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setCancelDialogOpen(false)} sx={{ textTransform: "none" }}>
            Keep editing
          </Button>
          <Button onClick={confirmCancelAndLeave} color="primary" variant="contained" sx={{ textTransform: "none" }}>
            Go back anyway
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CreateBillingRule;
